import { useState, useEffect, useCallback } from 'react'
import {
  FileText, RefreshCw, Download, AlertTriangle, Sparkles, Clock,
  Gauge, Timer, Swords, Target as TargetIcon, ShieldAlert,
} from 'lucide-react'
import { clsx } from 'clsx'
import { api } from '../lib/api'

type Posture = {
  nba?: {
    top_actions?: { title?: string; why_now?: string; category?: string }[]
    by_domain?: Record<string, number>
    total_actions?: number
    closing_fastest?: string | null
  }
  race?: { losing_count?: number; median_exposure_days?: number; ai_accelerated_count?: number }
  tte?: { median_tte_days?: number; trend?: string }
  purple?: { red_win_rate?: number | null; green_catch_rate?: number | null; green_blind?: boolean }
  target?: { drift_score?: number; behind_count?: number; worst_category?: string }
  ai_risk?: { incidents_total?: number; aisi_concern_models?: number; ai_actors?: number }
  underwriting?: { market_direction?: string; advisory_count?: number }
  ncsc?: { critical_count?: number }
  _errors?: string[]
}

type Narrative = {
  markdown: string
  posture: Posture
  generated_at: string
  model_used: string
  audience?: string
}

// ── Tiny, safe markdown → React renderer ─────────────────────────────────────
// Handles the subset the narrative emits: ## headings, numbered lists, blank-line
// paragraphs, and **bold** inline. No raw HTML is injected.
function renderInline(text: string, keyBase: string): React.ReactNode[] {
  const out: React.ReactNode[] = []
  const re = /\*\*(.+?)\*\*/g
  let last = 0
  let m: RegExpExecArray | null
  let i = 0
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) out.push(text.slice(last, m.index))
    out.push(<strong key={`${keyBase}-b${i++}`} className="text-text-primary font-semibold">{m[1]}</strong>)
    last = m.index + m[0].length
  }
  if (last < text.length) out.push(text.slice(last))
  return out
}

function Markdown({ md }: { md: string }) {
  const blocks: React.ReactNode[] = []
  const lines = (md || '').split('\n')
  let para: string[] = []
  let list: string[] = []
  let k = 0

  const flushPara = () => {
    if (para.length) {
      blocks.push(
        <p key={`p${k++}`} className="text-[13px] leading-relaxed text-text-secondary mb-3">
          {renderInline(para.join(' '), `p${k}`)}
        </p>,
      )
      para = []
    }
  }
  const flushList = () => {
    if (list.length) {
      blocks.push(
        <ol key={`l${k++}`} className="list-decimal pl-5 space-y-1.5 mb-3 text-[13px] leading-relaxed text-text-secondary">
          {list.map((it, idx) => <li key={idx}>{renderInline(it, `l${k}-${idx}`)}</li>)}
        </ol>,
      )
      list = []
    }
  }

  for (const raw of lines) {
    const line = raw.replace(/\s+$/, '')
    if (/^#{1,3}\s+/.test(line)) {
      flushPara(); flushList()
      const text = line.replace(/^#{1,3}\s+/, '')
      blocks.push(
        <h3 key={`h${k++}`} className="text-sm font-semibold text-text-primary uppercase tracking-wide mt-5 mb-2 first:mt-0">
          {text}
        </h3>,
      )
    } else if (/^\s*\d+[.)]\s+/.test(line)) {
      flushPara()
      list.push(line.replace(/^\s*\d+[.)]\s+/, ''))
    } else if (/^\s*[-*]\s+/.test(line)) {
      flushPara()
      list.push(line.replace(/^\s*[-*]\s+/, ''))
    } else if (line.trim() === '') {
      flushPara(); flushList()
    } else if (line.trim() === '---') {
      flushPara(); flushList()
      blocks.push(<hr key={`hr${k++}`} className="border-border my-4" />)
    } else {
      flushList()
      para.push(line)
    }
  }
  flushPara(); flushList()
  return <div>{blocks}</div>
}

function Tile({ label, value, hint, icon: Icon, tone }: {
  label: string; value: React.ReactNode; hint?: string; icon: React.ElementType; tone?: string
}) {
  return (
    <div className="card p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-text-muted">
        <Icon size={11} /> {label}
      </div>
      <div className={clsx('text-xl font-bold mt-1', tone || 'text-text-primary')}>{value}</div>
      {hint && <div className="text-[10px] text-text-muted mt-0.5 truncate">{hint}</div>}
    </div>
  )
}

function fmtTime(iso?: string): string {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString() } catch { return iso }
}

export default function ExecNarrativePage() {
  const [data, setData] = useState<Narrative | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [regenerating, setRegenerating] = useState(false)

  const load = useCallback(async (refresh = false) => {
    if (refresh) setRegenerating(true); else setLoading(true)
    setError('')
    try {
      const { data } = await api.get<Narrative>('/api/exec-narrative', {
        params: refresh ? { refresh: true } : {},
        timeout: 90_000,
      })
      setData(data)
    } catch {
      setError('Could not load the board narrative. Please retry.')
    } finally {
      setLoading(false); setRegenerating(false)
    }
  }, [])

  useEffect(() => { load(false) }, [load])

  const p = data?.posture || {}
  const race = p.race || {}
  const tte = p.tte || {}
  const target = p.target || {}
  const purple = p.purple || {}
  const nba = p.nba || {}
  const rwr = typeof purple.red_win_rate === 'number' ? `${Math.round(purple.red_win_rate * 100)}%` : '—'
  const topAction = nba.top_actions && nba.top_actions[0] ? nba.top_actions[0].title : '—'
  const llm = data && data.model_used && data.model_used !== 'templated' && data.model_used !== 'error'

  return (
    <div className="max-w-5xl mx-auto px-4 py-5">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4 flex-wrap">
        <div>
          <h1 className="text-xl font-bold text-text-primary flex items-center gap-2">
            <FileText size={20} className="text-cyan-light" />
            Executive Narrative — board pack
          </h1>
          <p className="text-[12px] text-text-muted mt-1">
            An LLM-synthesised, board-ready read of the whole security &amp; risk posture —
            the week's trend and the few things that matter. One-click markdown export for the
            Bank of England handover.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button
            type="button"
            onClick={() => load(true)}
            disabled={regenerating}
            className="text-[12px] px-3 py-1.5 rounded border border-border bg-surface-raised text-text-secondary hover:text-text-primary inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <RefreshCw size={13} className={clsx(regenerating && 'animate-spin')} />
            {regenerating ? 'Regenerating…' : 'Regenerate'}
          </button>
          <a
            href="/api/exec-narrative/export?format=markdown"
            className="text-[12px] px-3 py-1.5 rounded border border-cyan/40 bg-cyan/15 text-cyan-light hover:bg-cyan/25 inline-flex items-center gap-1.5"
          >
            <Download size={13} /> Download (markdown)
          </a>
        </div>
      </div>

      {/* Provenance line */}
      {data && (
        <div className="flex items-center gap-3 text-[11px] text-text-muted mb-4 flex-wrap">
          <span className="inline-flex items-center gap-1">
            <Clock size={11} /> Generated {fmtTime(data.generated_at)}
          </span>
          <span className={clsx(
            'inline-flex items-center gap-1 px-1.5 py-0.5 rounded border',
            llm ? 'border-purple/30 bg-purple/10 text-purple-light'
              : 'border-border bg-surface-raised text-text-muted',
          )}>
            <Sparkles size={10} />
            {llm ? `Written by ${data.model_used}` : 'Templated from live metrics'}
          </span>
          {p._errors && p._errors.length > 0 && (
            <span className="inline-flex items-center gap-1 text-amber-light">
              <AlertTriangle size={10} /> {p._errors.length} source(s) unavailable
            </span>
          )}
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="card p-8 text-center text-text-muted text-sm">
          <RefreshCw size={20} className="animate-spin mx-auto mb-3 text-cyan-light" />
          Reading the posture and writing the narrative…
        </div>
      )}

      {/* Error */}
      {!loading && error && (
        <div className="card p-6 text-center">
          <AlertTriangle size={22} className="mx-auto mb-2 text-rose-light" />
          <p className="text-sm text-text-secondary">{error}</p>
          <button type="button" onClick={() => load(false)}
                  className="mt-3 text-[12px] px-3 py-1.5 rounded border border-border bg-surface-raised text-text-secondary hover:text-text-primary">
            Retry
          </button>
        </div>
      )}

      {/* Empty */}
      {!loading && !error && (!data || !data.markdown) && (
        <div className="card p-8 text-center text-text-muted text-sm">
          No narrative has been generated yet. Click <strong>Regenerate</strong> to write one.
        </div>
      )}

      {/* Narrative */}
      {!loading && !error && data && data.markdown && (
        <>
          <article className="card p-5 mb-5">
            <Markdown md={data.markdown} />
          </article>

          {/* Posture at a glance */}
          <h2 className="text-[11px] font-semibold uppercase tracking-widest text-text-muted mb-2">
            Posture at a glance — the numbers behind the words
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 mb-3">
            <Tile label="Losing races" icon={Swords}
                  value={race.losing_count ?? '—'}
                  hint={race.median_exposure_days ? `~${race.median_exposure_days}d median exposure` : undefined}
                  tone={(race.losing_count || 0) > 0 ? 'text-rose-light' : 'text-emerald-light'} />
            <Tile label="Median TTE" icon={Timer}
                  value={tte.median_tte_days != null ? `${tte.median_tte_days}d` : '—'}
                  hint={tte.trend ? `trend: ${tte.trend}` : undefined}
                  tone={tte.trend === 'shrinking' ? 'text-amber-light' : undefined} />
            <Tile label="Drift score" icon={TargetIcon}
                  value={target.drift_score != null ? `${Math.round(target.drift_score)}` : '—'}
                  hint={target.behind_count != null ? `${target.behind_count} behind target` : undefined}
                  tone={(target.drift_score || 0) >= 50 ? 'text-amber-light' : undefined} />
            <Tile label="Red-win rate" icon={ShieldAlert}
                  value={rwr}
                  hint={purple.green_blind ? 'green blind' : 'attack simulation'}
                  tone={typeof purple.red_win_rate === 'number' && purple.red_win_rate >= 0.5 ? 'text-rose-light' : undefined} />
            <Tile label="AI incidents" icon={Gauge}
                  value={p.ai_risk?.incidents_total ?? '—'}
                  hint={p.ai_risk?.aisi_concern_models != null ? `${p.ai_risk.aisi_concern_models} model(s) flagged` : undefined} />
            <Tile label="Open actions" icon={FileText}
                  value={nba.total_actions ?? '—'}
                  hint={nba.closing_fastest || undefined} />
          </div>
          <div className="card p-3 text-[12px] text-text-secondary">
            <span className="text-text-muted">Top priority action: </span>
            <span className="font-medium text-text-primary">{topAction}</span>
            {p.underwriting?.market_direction && (
              <span className="text-text-muted">
                {'  ·  '}Cyber-insurance market: <span className="text-text-secondary">{p.underwriting.market_direction}</span>
              </span>
            )}
          </div>
        </>
      )}
    </div>
  )
}
